import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import { supabase } from "../lib/supabase";
import { downloadCsv, parseCsv } from "../lib/csv";
import { useOrganization } from "../organization/OrganizationProvider";

type ItemType = "product" | "service";
type ItemStatus = "active" | "inactive" | "archived";

type CatalogRow = {
  id: string;
  item_type: ItemType;
  sku: string | null;
  name: string;
  description: string | null;
  unit: string;
  sale_price: number | string;
  cost_price: number | string;
  tax_rate: number | string;
  track_inventory: boolean;
  status: ItemStatus;
  updated_at: string;
};

type SupplierOption = {
  id: string;
  name: string;
};

type CatalogMedia = {
  id: string;
  catalog_item_id: string;
  storage_path: string;
  original_filename: string | null;
  is_primary: boolean;
};

type CatalogForm = {
  itemType: ItemType;
  sku: string;
  name: string;
  description: string;
  unit: string;
  salePrice: string;
  costPrice: string;
  taxRate: string;
  trackInventory: boolean;
};

const EMPTY_FORM: CatalogForm = {
  itemType: "service",
  sku: "",
  name: "",
  description: "",
  unit: "unit",
  salePrice: "0",
  costPrice: "0",
  taxRate: "18",
  trackInventory: false,
};

function money(value: number | string) {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
  }).format(Number(value || 0));
}

function nullable(value: string) {
  const clean = value.trim();
  return clean ? clean : null;
}

export function CatalogPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [items, setItems] = useState<CatalogRow[]>([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"" | ItemType>("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CatalogForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  const [mediaBusyId, setMediaBusyId] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [supplierSelection, setSupplierSelection] = useState<Record<string, string>>({});
  const [supplierBusyId, setSupplierBusyId] = useState<string | null>(null);

  const loadCatalog = useCallback(
    async (query: string, itemType: "" | ItemType) => {
      if (!organizationId) {
        setItems([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError(null);

      const { data, error } = await supabase.rpc(
        "list_organization_catalog",
        {
          p_organization_id: organizationId,
          p_search: query.trim() || null,
          p_item_type: itemType || null,
        },
      );

      if (error) {
        setItems([]);
        setLoadError(error.message);
        setLoading(false);
        return;
      }

      setItems((data ?? []) as CatalogRow[]);
      setLoading(false);
    },
    [organizationId],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadCatalog(search, typeFilter);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [loadCatalog, search, typeFilter]);
  useEffect(() => {
    async function loadSuppliers() {
      if (!organizationId) {
        setSuppliers([]);
        return;
      }

      const { data } = await supabase.rpc("list_organization_suppliers", {
        p_organization_id: organizationId,
        p_search: null,
      });

      setSuppliers(
        ((data ?? []) as Array<SupplierOption & { status?: string }>).filter(
          (supplier) => !supplier.status || supplier.status === "active",
        ),
      );
    }

    void loadSuppliers();
  }, [organizationId]);

  useEffect(() => {
    let active = true;

    async function loadMedia() {
      if (!organizationId || items.length === 0) {
        if (active) setMediaUrls({});
        return;
      }

      const { data, error } = await supabase
        .from("catalog_item_media")
        .select("id,catalog_item_id,storage_path,original_filename,is_primary")
        .eq("organization_id", organizationId)
        .eq("status", "active")
        .order("is_primary", { ascending: false });

      if (error || !active) return;

      const next: Record<string, string> = {};
      for (const media of (data ?? []) as CatalogMedia[]) {
        if (next[media.catalog_item_id]) continue;
        const { data: signed } = await supabase.storage
          .from("catalog-media")
          .createSignedUrl(media.storage_path, 3600);
        if (signed?.signedUrl) next[media.catalog_item_id] = signed.signedUrl;
      }

      if (active) setMediaUrls(next);
    }

    void loadMedia();
    return () => {
      active = false;
    };
  }, [organizationId, items]);


  useEffect(() => {
    setSearch("");
    setTypeFilter("");
    setForm(EMPTY_FORM);
    setFormOpen(false);
    setEditingId(null);
    setActionError(null);
    setActionSuccess(null);
  }, [organizationId]);

  const summary = useMemo(
    () => ({
      products: items.filter((item) => item.item_type === "product").length,
      services: items.filter((item) => item.item_type === "service").length,
      active: items.filter((item) => item.status === "active").length,
    }),
    [items],
  );

  function resetForm() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormOpen(false);
  }

  function startEdit(item: CatalogRow) {
    setEditingId(item.id);
    setForm({
      itemType: item.item_type,
      sku: item.sku ?? "",
      name: item.name,
      description: item.description ?? "",
      unit: item.unit,
      salePrice: String(item.sale_price ?? 0),
      costPrice: String(item.cost_price ?? 0),
      taxRate: String(item.tax_rate ?? 0),
      trackInventory: item.track_inventory,
    });
    setFormOpen(true);
    setActionError(null);
    setActionSuccess(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!organizationId) return;

    const salePrice = Number(form.salePrice);
    const costPrice = Number(form.costPrice);
    const taxRate = Number(form.taxRate);

    if (!form.name.trim()) {
      setActionError("Ingresa un nombre.");
      return;
    }

    if (
      !Number.isFinite(salePrice) ||
      salePrice < 0 ||
      !Number.isFinite(costPrice) ||
      costPrice < 0 ||
      !Number.isFinite(taxRate) ||
      taxRate < 0 ||
      taxRate > 100
    ) {
      setActionError("Revisa precios e impuesto.");
      return;
    }

    setSaving(true);
    setActionError(null);
    setActionSuccess(null);

    const payload = {
      p_item_type: form.itemType,
      p_sku: nullable(form.sku),
      p_name: form.name,
      p_description: nullable(form.description),
      p_unit: form.unit.trim() || "unit",
      p_sale_price: salePrice,
      p_cost_price: costPrice,
      p_tax_rate: taxRate,
      p_track_inventory:
        form.itemType === "product" ? form.trackInventory : false,
    };

    const { error } = editingId
      ? await supabase.rpc("update_organization_catalog_item", {
          p_catalog_item_id: editingId,
          ...payload,
        })
      : await supabase.rpc("create_organization_catalog_item", {
          p_organization_id: organizationId,
          ...payload,
        });

    setSaving(false);

    if (error) {
      const duplicate =
        error.code === "23505" ||
        error.message.toLowerCase().includes("catalog_items_org_sku_uq");

      const trackingLocked =
        error.message.includes("CATALOG_INVENTORY_TRACKING_LOCKED_BY_HISTORY");
      const typeLocked =
        error.message.includes("CATALOG_ITEM_TYPE_LOCKED_BY_INVENTORY_HISTORY");

      setActionError(
        duplicate
          ? "Ya existe un artículo con ese SKU en esta empresa."
          : trackingLocked
            ? "No se puede desactivar el control de inventario porque el producto ya tiene stock o historial de movimientos."
            : typeLocked
              ? "No se puede cambiar el tipo porque el elemento ya tiene historial de inventario."
              : error.message,
      );
      return;
    }

    const wasEditing = Boolean(editingId);
    resetForm();
    setActionSuccess(
      wasEditing
        ? "Elemento actualizado correctamente."
        : "Elemento creado correctamente.",
    );
    await loadCatalog(search, typeFilter);
  }

  function downloadCatalogTemplate() {
    downloadCsv(
      "plantilla-catalogo.csv",
      [
        "item_type",
        "sku",
        "name",
        "description",
        "unit",
        "sale_price",
        "cost_price",
        "tax_rate",
        "track_inventory",
      ],
      [
        {
          item_type: "product",
          sku: "SKU-001",
          name: "Producto ejemplo",
          description: "Descripción opcional",
          unit: "unit",
          sale_price: "100",
          cost_price: "60",
          tax_rate: "18",
          track_inventory: "true",
        },
      ],
    );
  }

  async function handleCatalogCsv(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !organizationId) return;

    setBulkBusy(true);
    setActionError(null);
    setActionSuccess(null);

    const rows = parseCsv(await file.text());
    let created = 0;
    const errors: string[] = [];

    for (const [index, row] of rows.entries()) {
      const itemType = row.item_type === "service" ? "service" : "product";
      const salePrice = Number(row.sale_price || 0);
      const costPrice = Number(row.cost_price || 0);
      const taxRate = Number(row.tax_rate || 18);
      const trackInventory =
        itemType === "product" &&
        ["1", "true", "si", "sí", "yes"].includes(
          String(row.track_inventory || "").toLowerCase(),
        );

      if (!row.name?.trim()) {
        errors.push(`Fila ${index + 2}: falta name.`);
        continue;
      }

      const { error } = await supabase.rpc("create_organization_catalog_item", {
        p_organization_id: organizationId,
        p_item_type: itemType,
        p_sku: nullable(row.sku || ""),
        p_name: row.name.trim(),
        p_description: nullable(row.description || ""),
        p_unit: row.unit?.trim() || "unit",
        p_sale_price: Number.isFinite(salePrice) ? salePrice : 0,
        p_cost_price: Number.isFinite(costPrice) ? costPrice : 0,
        p_tax_rate: Number.isFinite(taxRate) ? taxRate : 18,
        p_track_inventory: trackInventory,
      });

      if (error) {
        errors.push(`Fila ${index + 2}: ${error.message}`);
      } else {
        created += 1;
      }
    }

    setBulkBusy(false);
    setActionSuccess(
      errors.length
        ? `Carga terminada: ${created} creados, ${errors.length} con error.`
        : `Carga masiva completada: ${created} elementos creados.`,
    );
    if (errors.length) setActionError(errors.slice(0, 5).join(" "));
    await loadCatalog(search, typeFilter);
  }

  async function uploadCatalogImage(
    item: CatalogRow,
    event: ChangeEvent<HTMLInputElement>,
  ) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !organizationId) return;

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setActionError("La imagen debe ser JPG, PNG o WEBP.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setActionError("La imagen no debe superar 5 MB.");
      return;
    }

    setMediaBusyId(item.id);
    setActionError(null);
    setActionSuccess(null);

    const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `${organizationId}/${item.id}/${Date.now()}.${extension}`;

    const { error: uploadError } = await supabase.storage
      .from("catalog-media")
      .upload(path, file, { upsert: false });

    if (uploadError) {
      setMediaBusyId(null);
      setActionError(uploadError.message);
      return;
    }

    const { error: recordError } = await supabase
      .from("catalog_item_media")
      .insert({
        organization_id: organizationId,
        catalog_item_id: item.id,
        storage_bucket: "catalog-media",
        storage_path: path,
        original_filename: file.name,
        mime_type: file.type,
        file_size: file.size,
        is_primary: true,
      });

    if (recordError) {
      await supabase.storage.from("catalog-media").remove([path]);
      setMediaBusyId(null);
      setActionError(recordError.message);
      return;
    }

    const { data: signed } = await supabase.storage
      .from("catalog-media")
      .createSignedUrl(path, 3600);

    setMediaBusyId(null);
    if (signed?.signedUrl) {
      setMediaUrls((current) => ({ ...current, [item.id]: signed.signedUrl }));
    }
    setActionSuccess("Imagen de referencia cargada correctamente.");
  }

  async function assignSupplier(item: CatalogRow) {
    if (!organizationId) return;
    const supplierId = supplierSelection[item.id];

    if (!supplierId) {
      setActionError("Selecciona un proveedor.");
      return;
    }

    setSupplierBusyId(item.id);
    setActionError(null);
    setActionSuccess(null);

    const { error } = await supabase
      .from("catalog_item_suppliers")
      .upsert(
        {
          organization_id: organizationId,
          catalog_item_id: item.id,
          supplier_id: supplierId,
          preferred: true,
        },
        { onConflict: "catalog_item_id,supplier_id" },
      );

    setSupplierBusyId(null);

    if (error) {
      setActionError(error.message);
      return;
    }

    setActionSuccess("Proveedor asociado al SKU.");
  }

  async function changeStatus(item: CatalogRow) {
    const nextStatus = item.status === "active" ? "inactive" : "active";

    setBusyId(item.id);
    setActionError(null);
    setActionSuccess(null);

    const { error } = await supabase.rpc("set_catalog_item_status", {
      p_catalog_item_id: item.id,
      p_status: nextStatus,
    });

    setBusyId(null);

    if (error) {
      setActionError(error.message);
      return;
    }

    setActionSuccess(
      nextStatus === "active" ? "Elemento activado." : "Elemento desactivado.",
    );
    await loadCatalog(search, typeFilter);
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
          <p>Necesitas una empresa activa para trabajar con el catálogo.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Catálogo</span>
          <h1>{selectedOrganization.name}</h1>
          <p>Productos y servicios reutilizables en toda la operación.</p>
        </div>

        <div className="page-header-buttons">
          <button
            className="ghost-button"
            type="button"
            onClick={downloadCatalogTemplate}
          >
            Descargar plantilla
          </button>
          <label className="ghost-button file-action-button">
            {bulkBusy ? "Importando…" : "Carga masiva CSV"}
            <input
              type="file"
              accept=".csv,text/csv"
              disabled={bulkBusy}
              onChange={(event) => void handleCatalogCsv(event)}
            />
          </label>
          <button
            className="primary-button"
            type="button"
            onClick={() => {
              if (formOpen) {
                resetForm();
              } else {
                setEditingId(null);
                setForm(EMPTY_FORM);
                setFormOpen(true);
              }
              setActionError(null);
              setActionSuccess(null);
            }}
          >
            {formOpen ? "Cerrar" : "Nuevo elemento"}
          </button>
        </div>
      </header>

      <section className="catalog-summary-grid">
        <div className="metric-card">
          <span>Productos</span>
          <strong>{loading ? "…" : summary.products}</strong>
        </div>
        <div className="metric-card">
          <span>Servicios</span>
          <strong>{loading ? "…" : summary.services}</strong>
        </div>
        <div className="metric-card">
          <span>Activos</span>
          <strong>{loading ? "…" : summary.active}</strong>
        </div>
      </section>

      {formOpen ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">
                {editingId ? "Edición" : "Registro"}
              </span>
              <h2>
                {editingId
                  ? "Editar producto o servicio"
                  : "Nuevo producto o servicio"}
              </h2>
            </div>
          </div>

          <form className="catalog-form" onSubmit={handleCreate}>
            <label>
              Tipo
              <select
                value={form.itemType}
                onChange={(event) => {
                  const itemType = event.target.value as ItemType;
                  setForm((current) => ({
                    ...current,
                    itemType,
                    trackInventory:
                      itemType === "product" ? current.trackInventory : false,
                  }));
                }}
              >
                <option value="service">Servicio</option>
                <option value="product">Producto</option>
              </select>
            </label>

            <label>
              SKU
              <input
                value={form.sku}
                onChange={(event) =>
                  setForm((current) => ({ ...current, sku: event.target.value }))
                }
              />
            </label>

            <label className="field-span-2">
              Nombre
              <input
                required
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, name: event.target.value }))
                }
              />
            </label>

            <label className="field-span-2">
              Descripción
              <input
                value={form.description}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Unidad
              <input
                value={form.unit}
                onChange={(event) =>
                  setForm((current) => ({ ...current, unit: event.target.value }))
                }
              />
            </label>

            <label>
              Precio venta
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.salePrice}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    salePrice: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Costo
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.costPrice}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    costPrice: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              IGV %
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.taxRate}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    taxRate: event.target.value,
                  }))
                }
              />
            </label>

            {form.itemType === "product" ? (
              <label className="catalog-checkbox">
                <input
                  type="checkbox"
                  checked={form.trackInventory}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      trackInventory: event.target.checked,
                    }))
                  }
                />
                Controlar inventario
              </label>
            ) : null}

            <div className="form-actions field-span-full">
              <button className="primary-button" type="submit" disabled={saving}>
                {saving
                  ? "Guardando…"
                  : editingId
                    ? "Guardar cambios"
                    : "Guardar"}
              </button>
              {editingId ? (
                <button
                  className="ghost-button"
                  type="button"
                  disabled={saving}
                  onClick={resetForm}
                >
                  Cancelar edición
                </button>
              ) : null}
            </div>
          </form>
        </section>
      ) : null}

      {actionError ? (
        <div className="inline-alert" role="alert">
          {actionError}
        </div>
      ) : null}

      {actionSuccess ? (
        <div className="form-success catalog-message">{actionSuccess}</div>
      ) : null}

      <section className="catalog-toolbar">
        <div className="catalog-filter-buttons">
          <button
            type="button"
            className={typeFilter === "" ? "compact-button active-filter" : "compact-button"}
            onClick={() => setTypeFilter("")}
          >
            Todos
          </button>
          <button
            type="button"
            className={typeFilter === "product" ? "compact-button active-filter" : "compact-button"}
            onClick={() => setTypeFilter("product")}
          >
            Productos
          </button>
          <button
            type="button"
            className={typeFilter === "service" ? "compact-button active-filter" : "compact-button"}
            onClick={() => setTypeFilter("service")}
          >
            Servicios
          </button>
        </div>

        <input
          type="search"
          placeholder="Buscar por nombre, SKU o descripción"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </section>

      {loadError ? (
        <div className="inline-alert" role="alert">
          {loadError}
        </div>
      ) : null}

      {!loading && items.length === 0 && !loadError ? (
        <section className="empty-state">
          <strong>No hay elementos para mostrar.</strong>
          <p>Crea el primer producto o servicio del catálogo.</p>
        </section>
      ) : null}

      <section className="catalog-grid" aria-label="Catálogo">
        {items.map((item) => (
          <article className="catalog-card" key={item.id}>
            <div className="catalog-card-heading">
              <div>
                <span className="status-badge">
                  {item.item_type === "product" ? "Producto" : "Servicio"}
                </span>
                <h2>{item.name}</h2>
                <p>{item.sku ?? "Sin SKU"}</p>
              </div>
              <strong>{money(item.sale_price)}</strong>
            </div>

            <div className="catalog-media-block">
              {mediaUrls[item.id] ? (
                <img
                  className="catalog-media-preview"
                  src={mediaUrls[item.id]}
                  alt={`Referencia de ${item.name}`}
                />
              ) : (
                <div className="catalog-media-placeholder">Sin imagen</div>
              )}

              <label className="compact-button file-action-button">
                {mediaBusyId === item.id ? "Subiendo…" : "Subir imagen"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={mediaBusyId === item.id}
                  onChange={(event) => void uploadCatalogImage(item, event)}
                />
              </label>
            </div>

            <p className="catalog-description">
              {item.description ?? "Sin descripción"}
            </p>

            <dl className="customer-meta">
              <div>
                <dt>Costo</dt>
                <dd>{money(item.cost_price)}</dd>
              </div>
              <div>
                <dt>IGV</dt>
                <dd>{Number(item.tax_rate)}%</dd>
              </div>
              <div>
                <dt>Unidad</dt>
                <dd>{item.unit}</dd>
              </div>
              <div>
                <dt>Inventario</dt>
                <dd>{item.track_inventory ? "Sí" : "No"}</dd>
              </div>
            </dl>

            {item.item_type === "product" ? (
              <div className="catalog-supplier-control">
                <select
                  value={supplierSelection[item.id] ?? ""}
                  onChange={(event) =>
                    setSupplierSelection((current) => ({
                      ...current,
                      [item.id]: event.target.value,
                    }))
                  }
                >
                  <option value="">Proveedor para reposición</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
                <button
                  className="compact-button"
                  type="button"
                  disabled={supplierBusyId === item.id}
                  onClick={() => void assignSupplier(item)}
                >
                  {supplierBusyId === item.id ? "Guardando…" : "Asociar proveedor"}
                </button>
              </div>
            ) : null}

            <div className="catalog-card-footer">
              <span
                className={
                  item.status === "active"
                    ? "status-badge status-badge-approved"
                    : "status-badge"
                }
              >
                {item.status === "active" ? "Activo" : "Inactivo"}
              </span>

              <div className="quotation-actions">
                <button
                  className="compact-button"
                  type="button"
                  disabled={busyId === item.id}
                  onClick={() => startEdit(item)}
                >
                  Editar
                </button>
                <button
                  className="compact-button"
                  type="button"
                  disabled={busyId === item.id}
                  onClick={() => void changeStatus(item)}
                >
                  {busyId === item.id
                    ? "Actualizando…"
                    : item.status === "active"
                      ? "Desactivar"
                      : "Activar"}
                </button>
              </div>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
